import { Fragment, type ReactNode, useCallback, useMemo } from 'react';

import type { DeckMaterialItem, DeckMaterialRef } from '../../../deck/materials';
import {
  activeTabIndex,
  findMaterial,
  isMaterialOpen,
  orderMaterialTabs,
} from '../../../deck/materials';
import type { IconComponent } from '../../components/Button';
import { MarqueeLabel } from '../../components/MarqueeLabel';
import { resolveSegmentIconColor } from '../../components/SegmentedControl/SegmentedControl.icons';
import { segmentTextVariants } from '../../components/SegmentedControl/SegmentedControl.variants';
import { CircleX } from '../../icons/custom';
import type { PressEvent } from '../../primitives/Pressable';
import { Pressable } from '../../primitives/Pressable';
import { ScrollRow } from '../../primitives/ScrollRow';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { DARK_TRACK } from '../../styles/elevation';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import type { MaterialsTabBarProps } from './DeckMaterials.types';
import { DECK_MATERIAL_ICONS, MATERIALS_OVERVIEW_ICON } from './icons';

/**
 * The source pane's tab bar — Figma `44:1119` / `44:1194`, copied to the frame.
 *
 * All Sources · divider · the open tabs · divider · "+". Each rule belongs to
 * the group AFTER it: no tabs, no first rule; no "+", no second — the strip
 * never ends at a dangling rule. And the track keeps ONE height whether the
 * "+" is in it or not, because on web the "+" comes and goes with the view.
 *
 * WHAT COMES FROM THE DESIGN SYSTEM: the track is the segmented control's own
 * shell, the type ramp is `segmentTextVariants`, the icon colours are
 * `resolveSegmentIconColor`, and the "+" is a DS `Button` (secondary, `xs` —
 * the 32px icon-only size the frame draws). Those follow the DS when it moves.
 *
 * WHAT THE DESIGN SYSTEM HAS NO EQUIVALENT FOR, and is therefore spelled out
 * here against the frame rather than approximated:
 *
 *  - A DIVIDER INSIDE the track. `SegmentedControl` has no separator between
 *    segments at all.
 *  - A CLOSE AFFORDANCE inside a cell. `Segment` has no trailing slot, and the
 *    one hole that exists (`label: ReactNode`) would nest a button inside a
 *    button, which the browser re-parents.
 *  - THE CELL PADDING. The frame's cells carry that close icon, so they are
 *    12/8 (inactive) and 8/8 (active) rather than the DS `m` ramp's 12/16 —
 *    the DS number is for a cell with nothing after the label.
 *  - THE TRACK GAP. 8px here (it separates GROUPS — chip, rule, tabs, rule,
 *    button) against the DS's 2px between adjacent segments.
 *
 * Everything above is a deliberate divergence from the DS, not a drift: the
 * frame is the spec, and these are the places it asks for something the
 * component cannot express.
 *
 * THE TABS ARE DRAWN IN THE DECK'S ORDER (`orderMaterialTabs`), not the order
 * they were opened in: the strip is a view of the deck's materials list, the
 * same list the All Sources grid draws, and dragging a tab (or a tile) to a
 * new place edits THAT list — so the order is the deck's, follows the user to
 * the other device and into the group, and the strip and the grid can never
 * disagree. The drag itself is the platform's, injected through `sortable`.
 * All Sources and the "+" are not materials: they stay where they are and
 * nothing can be dropped on them.
 */

/**
 * Figma: `h-[28px] rounded-[11px] gap-[6px]` on every cell of this bar.
 *
 * `flex` IS LOAD-BEARING and must stay in front of `flex-row`. A cell is a
 * `Pressable`, which on web is a `<button>`, and the Pressable primitive's
 * reset declares no `display` at all (unlike `View`, whose reset is
 * `flex flex-col`) — so the button keeps the browser's `inline-block` and
 * `flex-row` sets a flex-direction on a box that is not a flex container,
 * which does nothing. The label then lays out as a BLOCK, because
 * `<Text numberOfLines={1}>` compiles to `[display:-webkit-box]`, and drops
 * under the icon. That is the stacking bug. The DS's own `Button` avoids it
 * the same way, by saying `inline-flex flex-row` in its variants.
 */
const CELL = 'h-7 flex flex-row items-center justify-center gap-1.5 rounded-[11px]';
/** Figma `44:1161`: white fill, secondary-200 hairline, 0 2px 4px /6%. */
// Dark's lifted step: web wears the translucent control step; native the
// OPAQUE `secondary-200` — a translucent fill is runtime-processed on native
// and must not toggle on a live cell, an opaque one may (the iPad's source
// strip has dark mode now).
const CELL_ACTIVE =
  'bg-secondary-50 border border-secondary-200 web:shadow-[0px_2px_4px_0px_rgba(0,0,0,0.06)] web:dark:bg-secondary-200/60 native:dark:bg-secondary-200';
const CELL_INACTIVE = 'border border-transparent';

/*
 * NO 50% OPACITY ON AN INACTIVE CELL, although Figma `44:1168` draws one.
 * Faded on top of the DS's own inactive tone, the label is `secondary-500` at
 * half strength — about 2:1 against the track, far under WCAG AA's 4.5:1, and
 * axe failed the deck page on it (All Sources, and every background tab's
 * name). An inactive cell now reads exactly like an inactive segment of the
 * DS `SegmentedControl`: `secondary-500` text and the DS inactive icon colour,
 * at full opacity (2026-10-09).
 */

export function MaterialsTabBar({
  items,
  state,
  overviewLabel,
  closeLabel,
  onSelect,
  onClose,
  onShowOverview,
  addControl,
  maxLabelWidth = 128,
  tabTestID,
  accessibilityLabel,
  renderEdgeFade,
  renderFadeMask,
  sortable,
  className,
  testID,
}: MaterialsTabBarProps) {
  const { isDark } = useTheme();
  const overview = state.view === 'overview';
  const tabs = useMemo(() => orderMaterialTabs(state.tabs, items), [state.tabs, items]);
  const tabKeys = useMemo(() => tabs.map(refKey), [tabs]);

  return (
    // `min-w-0 shrink` is what lets the strip scroll AT ALL. Every shared View
    // is `flex-shrink: 0` by default (the preset's `.fk-view` base rule, which
    // mirrors RN/Yoga), so without this the bar is laid out at its full
    // content width and `overflow-x: auto` has nothing to scroll.
    <View
      className={cn('min-w-0 shrink flex-row items-center', className)}
      testID={testID}>
      {/* The track — Figma `44:1119`.

          Its height is FIXED at what it is WITH the "+" in it: 32 (the `xs`
          button, the tallest thing it ever holds) + 4 padding each side + the
          hairline each side = 42. Without this it is 4px shorter whenever the
          "+" is absent — and on web the "+" leaves for the grid on All Sources
          and comes back with an open material, so the header would jump with
          every switch. The 28px cells centre in it either way. */}
      <View
        className={cn(
          'min-h-[42px] min-w-0 shrink flex-row items-center gap-2 rounded-[16px] border border-secondary-200 bg-secondary-50/70 p-1 web:shadow-[inset_0px_2px_6px_0px_rgba(0,0,0,0.1)]',
          // Dark: nested track, lifted active cell — the same ladder as the DS
          // SegmentedControl it mirrors (elevation.ts).
          DARK_TRACK,
        )}
        accessibilityLabel={accessibilityLabel}>
        <Cell
          icon={MATERIALS_OVERVIEW_ICON}
          label={overviewLabel}
          active={overview}
          isDark={isDark}
          onPress={onShowOverview}
          // Figma `44:1144`: symmetric 12, no trailing icon on this one.
          padding="px-3"
          testID={testID ? `${testID}-overview` : undefined}
        />

        {state.tabs.length > 0 ? (
          <>
            <Divider testID={testID ? `${testID}-divider` : undefined} />
            {/* Tabs are wider than plain segments — icon, name AND a close
                button each — so the strip overflows sooner. Only the TABS
                scroll: All Sources and the "+" sit outside the scroller, so
                neither can be scrolled out of reach. */}
            <ScrollRow
              activeIndex={activeTabIndex(state, items)}
              itemCount={tabs.length}
              fadeEdges
              // Web masks the tabs themselves (ScrollRow), so the fade lands
              // on the track's fill in every theme; native hosts pass their
              // own mask or band.
              renderEdgeFade={renderEdgeFade}
              renderFadeMask={renderFadeMask}
              scrollRef={sortable?.scrollRef}
              testID={testID ? `${testID}-strip` : undefined}>
              {/* Figma `44:1160`: the tabs sit flush against each other.
                  The sortable wraps the CELLS, inside the row: web's context
                  draws nothing, and the phone's sortable lays its own
                  children out, so it must be handed the cells themselves. */}
              <View className="flex-row items-center">
                {wrapList(
                  sortable,
                  tabs.map((ref) => {
                    const item = findMaterial(items, ref);
                    const tab = (
                      <MaterialTab
                        key={refKey(ref)}
                        tabRef={ref}
                        item={item}
                        active={isMaterialOpen(state, ref)}
                        isDark={isDark}
                        maxLabelWidth={maxLabelWidth}
                        closeLabel={closeLabel}
                        onSelect={onSelect}
                        onClose={onClose}
                        testID={tabTestID?.(ref)}
                      />
                    );
                    // A tab whose material the items do not hold yet draws
                    // nothing, so there is nothing to drag either.
                    return sortable && item ? (
                      <Fragment key={refKey(ref)}>
                        {sortable.wrapItem(tab, item)}
                      </Fragment>
                    ) : (
                      tab
                    );
                  }),
                  tabKeys,
                )}
              </View>
            </ScrollRow>
          </>
        ) : null}

        {/* The rule before the "+" belongs to the "+". Without an add control
            — a reader who cannot add, or web's All Sources, where the "+" is
            the grid's tile — the strip ends at its last tab. With no tabs the
            chip sits beside the "+" with nothing between them, as before. */}
        {addControl ? (
          <>
            {state.tabs.length > 0 ? (
              <Divider testID={testID ? `${testID}-divider` : undefined} />
            ) : null}
            {addControl}
          </>
        ) : null}
      </View>
    </View>
  );
}
(MaterialsTabBar as { displayName?: string }).displayName = 'MaterialsTabBar';

function wrapList(
  sortable: MaterialsTabBarProps['sortable'],
  list: ReactNode,
  keys: readonly string[],
): ReactNode {
  return sortable ? sortable.wrapList(list, keys) : list;
}

/** Figma `44:1158` / `44:1182`: a 1px rule the height of the track's content. */
function Divider({ testID }: { testID?: string }) {
  return <View className="h-5 w-px shrink-0 bg-secondary-200" testID={testID} />;
}

function refKey(ref: DeckMaterialRef): string {
  switch (ref.type) {
    case 'source':
      return `source:${ref.sourceId}`;
    case 'note':
      return `note:${ref.noteId}`;
    case 'mindmap':
      return `mindmap:${ref.mindmapId}`;
  }
}

/** One cell of the bar, in the frame's geometry. */
function Cell({
  icon: Icon,
  label,
  active,
  isDark,
  onPress,
  padding,
  labelMaxWidth,
  accessibilityLabel,
  testID,
}: {
  icon: IconComponent;
  label: string;
  active: boolean;
  isDark: boolean;
  onPress: () => void;
  padding: string;
  /** Clamp the name to this width and marquee it on hover / press-and-hold,
   *  like every other clamped name in the app. Absent: the label fits. */
  labelMaxWidth?: number;
  accessibilityLabel?: string;
  testID?: string;
}) {
  // The DS type ramp — B3 (Mona Sans Medium 12), which is what the frame's
  // cells use.
  const textClass = cn(
    segmentTextVariants({ size: 'm' }),
    active ? 'text-secondary-800' : 'text-fg-subtle',
  );
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      className={cn(CELL, padding, active ? CELL_ACTIVE : CELL_INACTIVE)}
      testID={testID}>
      <Icon size={16} color={resolveSegmentIconColor(active, 'tertiary', isDark)} />
      {labelMaxWidth != null ? (
        <MarqueeLabel label={label} maxWidth={labelMaxWidth} className={textClass} />
      ) : (
        <Text numberOfLines={1} className={textClass}>
          {label}
        </Text>
      )}
    </Pressable>
  );
}

function MaterialTab({
  tabRef,
  item,
  active,
  isDark,
  maxLabelWidth,
  closeLabel,
  onSelect,
  onClose,
  testID,
}: {
  tabRef: DeckMaterialRef;
  /** Absent while the deck's items are still catching up with the tab set. */
  item: DeckMaterialItem | undefined;
  active: boolean;
  isDark: boolean;
  maxLabelWidth: number;
  closeLabel: string;
  onSelect: (ref: DeckMaterialRef) => void;
  onClose: (ref: DeckMaterialRef) => void;
  testID?: string;
}) {
  const select = useCallback(() => onSelect(tabRef), [onSelect, tabRef]);
  const close = useCallback(
    (event: PressEvent) => {
      // The X sits over the cell; closing must not also select it.
      event.stopPropagation();
      onClose(tabRef);
    },
    [onClose, tabRef],
  );

  if (!item) return null;

  return (
    // `group` so the cell's hover reveals the close on an inactive tab.
    <View className="group relative flex-row items-center">
      <Cell
        icon={DECK_MATERIAL_ICONS[item.kind]}
        // The WHOLE name: the clamp is the label's, so hovering a long one
        // (press-and-hold on touch) scrolls the rest of it into view.
        label={item.label}
        labelMaxWidth={maxLabelWidth}
        accessibilityLabel={item.label}
        active={active}
        isDark={isDark}
        onPress={select}
        // One left inset in BOTH states (Figma's inactive 12): switching the
        // selected tab must not nudge its label (review 2026-10 — Figma's
        // active `44:1161` was 8, which shifted it). Both then reserve the
        // close icon's own column.
        padding="pl-3 pr-7"
        testID={testID}
      />

      {/* OUTSIDE the cell's pressable, overlaying it: a button inside a button
          is invalid HTML — the browser re-parents it, which breaks the click
          target and the accessibility tree at once.

          The ACTIVE tab always shows its close; an inactive one reveals it on
          hover. Nothing moves when it appears: the affordance is absolutely
          positioned (out of flow entirely) and the cell reserves its column
          with `pr-7` in both states, so the reveal is a pure opacity change.
          `web:`-scoped for the same reason the tile's ⋯ is — RN has no hover,
          so on the phone and the iPad every tab shows its close.

          HIDDEN MEANS INERT. `web:` gates the platform, NOT the input device:
          a touch-only BROWSER (iPad Safari, Android Chrome) runs the web build
          and never fires hover, so without `pointer-events-none` this would be
          an invisible live button — tapping the right end of a background tab
          would CLOSE it instead of opening it. With it, that tap falls through
          to the cell underneath and selects the tab; its close is then on
          screen, because the focused tab always shows one. Keyboard is
          unaffected — `pointer-events` does not gate focus, so tabbing to it
          still reveals and still activates it. */}
      <Pressable
        onPress={close}
        accessibilityRole="button"
        accessibilityLabel={closeLabel}
        className={cn(
          // `flex` for the same reason CELL needs it — a Pressable is not a
          // flex container on web, so the centring below is otherwise inert.
          'absolute right-2 flex size-4 items-center justify-center',
          !active &&
            'web:pointer-events-none web:opacity-0 web:transition-opacity ' +
              'web:group-hover:pointer-events-auto web:group-hover:opacity-100 ' +
              'web:focus-visible:opacity-100',
        )}
        testID={testID ? `${testID}-close` : undefined}>
        {/* secondary-600, read from the palette rather than written down, so
            it inverts in dark mode with every other token. */}
        <CircleX size={16} color={brandColor('secondary', '600', isDark)} />
      </Pressable>
    </View>
  );
}
